/* organization/Settings — enterprise org settings; 7 tabs; URL-routed; RBAC-gated */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill, ConfirmDialog } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, TabBar } from '../platform/_ui';
import * as orgApi from '../api/organization.api.js';
import { orgPoliciesApi } from '../api/org-policies.api.js';
import { org as orgEmailProviderApi } from '../api/email-provider.api.js';
import EmailProviderManager from '../shared/email/EmailProviderManager.jsx';
import { usePermissions } from './auth/usePermissions.js';
import { useOrganization, useOrgRuntime, useOrgFeatures } from './OrganizationRuntimeContext.jsx';
import { DeniedState } from './auth/DeniedState.jsx';
import { ORG } from './auth/capabilities.js';

const TABS = [
  { id: 'general',       label: 'General',        icon: 'Settings'      },
  { id: 'regional',      label: 'Regional',        icon: 'Globe'         },
  { id: 'security',      label: 'Security',        icon: 'Shield'        },
  { id: 'communication', label: 'Communication',   icon: 'MessageSquare' },
  { id: 'data',          label: 'Data & Privacy',  icon: 'HardDrive'     },
  { id: 'system',        label: 'System',          icon: 'Monitor'       },
  { id: 'danger',        label: 'Danger Zone',     icon: 'AlertTriangle' },
];

const Toggle = ({ on, onChange, disabled }) => (
  <button type="button" className={'nx-toggle' + (on ? ' is-on' : '')}
    onClick={() => !disabled && onChange(!on)} role="switch" aria-checked={on}
    disabled={disabled}>
    <span className="nx-toggle-knob" />
  </button>
);

const SectionCard = ({ title, desc, children }) => (
  <div className="nx-panel" style={{ padding: 24, marginBottom: 16, maxWidth: 720 }}>
    {(title || desc) && (
      <div style={{ marginBottom: 20 }}>
        {title && <h3 className="nx-panel-title">{title}</h3>}
        {desc && <p className="nx-muted" style={{ marginTop: 4, fontSize: 12, lineHeight: 1.5 }}>{desc}</p>}
      </div>
    )}
    {children}
  </div>
);

const RefCard = ({ icon, title, desc, linkLabel, onClick }) => (
  <div className="nx-panel" style={{ padding: '12px 20px', marginBottom: 16, maxWidth: 720, display: 'flex', alignItems: 'center', gap: 12 }}>
    <div style={{ width: 32, height: 32, borderRadius: 6, background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <NexusIcon name={icon} size={14} />
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{title}</div>
      {desc && <div className="nx-muted nx-mini">{desc}</div>}
    </div>
    <Btn variant="ghost" size="sm" icon="ArrowRight" onClick={onClick}>{linkLabel || 'Open'}</Btn>
  </div>
);

const ToggleRow = ({ label, desc, on, onChange, disabled }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--nx-divider)' }}>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
      {desc && <div className="nx-muted nx-mini">{desc}</div>}
    </div>
    <Toggle on={on} onChange={onChange} disabled={disabled} />
  </div>
);

const PolicyStatusRow = ({ icon, label, desc, on, last }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: last ? 'none' : '1px solid var(--nx-divider)' }}>
    <div style={{ width: 28, height: 28, borderRadius: 6, background: on ? 'var(--nx-success-tint)' : 'var(--nx-surface-alt)', color: on ? 'var(--nx-success)' : 'var(--nx-text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <NexusIcon name={icon} size={13} />
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
      {desc && <div className="nx-muted nx-mini" style={{ marginTop: 2 }}>{desc}</div>}
    </div>
    <Pill tone={on ? 'green' : 'gray'}>{on ? 'Enabled' : 'Not enabled'}</Pill>
  </div>
);

const buildPasswordRotationDesc = (days) =>
  days > 0 ? `Passwords must be changed every ${days} days.` : 'No password expiry configured.';

const buildPasswordComplexityDesc = (p) => {
  const parts = [];
  if (p?.passwordMinLength)       parts.push(`min ${p.passwordMinLength} chars`);
  if (p?.passwordRequireUppercase) parts.push('uppercase');
  if (p?.passwordRequireLowercase) parts.push('lowercase');
  if (p?.passwordRequireNumbers)   parts.push('numbers');
  if (p?.passwordRequireSymbols)   parts.push('symbols');
  return parts.length ? `Requires: ${parts.join(', ')}.` : 'No complexity requirements configured.';
};

const SecurityPoliciesContent = ({ policiesLoaded, policiesError, policies }) => {
  const sessionTimeoutOn   = (policies?.sessionTimeoutMinutes ?? 0) > 0;
  const sessionTimeoutDesc = sessionTimeoutOn
    ? `Inactive sessions are terminated after ${policies.sessionTimeoutMinutes} minutes.`
    : 'Members remain signed in until they manually sign out.';
  const passwordRotationOn  = (policies?.passwordRotationDays ?? 0) > 0;
  const hasComplexity = !!(
    policies?.passwordMinLength ||
    policies?.passwordRequireUppercase ||
    policies?.passwordRequireLowercase ||
    policies?.passwordRequireNumbers ||
    policies?.passwordRequireSymbols
  );
  if (!policiesLoaded) return (
    <div className="nx-empty" style={{ padding: '16px 0' }}>
      <NexusIcon name="Loader" size={16} /><span>Loading security policies…</span>
    </div>
  );
  if (policiesError) return (
    <div role="alert" style={{ padding: '14px 16px', borderRadius: 8, background: 'var(--nx-danger-tint)', border: '1px solid var(--nx-danger)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      <NexusIcon name="AlertCircle" size={14} style={{ color: 'var(--nx-danger)', flexShrink: 0, marginTop: 1 }} />
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-danger)' }}>Unable to load security policies</div>
        <div style={{ fontSize: 12, color: 'var(--nx-danger)', marginTop: 2 }}>Verify your connection or contact your platform administrator.</div>
      </div>
    </div>
  );
  return (
    <>
      <PolicyStatusRow icon="Smartphone" label="Multi-Factor Authentication (MFA)" desc="All workspace members must complete MFA verification on every login." on={policies?.mfaRequired ?? false} />
      <PolicyStatusRow icon="Lock" label="Single Sign-On (SSO)" desc="Members authenticate through an external identity provider via SAML or OIDC." on={policies?.ssoEnabled ?? false} />
      <PolicyStatusRow icon="UserPlus" label="Automated User Provisioning" desc="Member accounts are created and deactivated automatically via SCIM integration." on={policies?.autoProvision ?? false} />
      <PolicyStatusRow icon="Clock" label="Session Timeout" desc={sessionTimeoutDesc} on={sessionTimeoutOn} />
      <PolicyStatusRow icon="RefreshCw" label="Password Rotation" desc={buildPasswordRotationDesc(policies?.passwordRotationDays ?? 0)} on={passwordRotationOn} />
      <PolicyStatusRow icon="KeyRound" label="Password Complexity" desc={buildPasswordComplexityDesc(policies)} on={hasComplexity} last />
    </>
  );
};

function parseFeatureOverrides(raw) {
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : (raw ?? { add: [], remove: [] });
  } catch { return { add: [], remove: [] }; }
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return iso; }
}

export default function Settings() {
  const { orgId }    = useParams();
  const navigate     = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast        = useToast();
  const perm         = usePermissions();
  const { loading: runtimeLoading, refreshRuntime } = useOrgRuntime();
  const org          = useOrganization();
  const features     = useOrgFeatures();

  const tab    = searchParams.get('tab') || 'general';
  const setTab = (id) => setSearchParams({ tab: id }, { replace: true });
  const handleTabChange = (id) => { setTab(id); };

  /* ── Settings state ─────────────────────────────────────────────── */
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [localeForm, setLocaleForm] = useState({
    language: 'English (US)', dateFormat: 'DD/MM/YYYY',
    currency: 'INR (₹)', firstDayOfWeek: 'Monday', timezone: 'IST (UTC+5:30)',
  });
  const [notifForm, setNotifForm] = useState({
    notifyInvite: true, notifyLogin: true,
    notifyLicense: true, notifyAudit: false,
  });
  const notifInitRef = useRef({ notifyInvite: true, notifyLogin: true, notifyLicense: true, notifyAudit: false });
  const [notifDirty, setNotifDirty] = useState(false);
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain]     = useState('');

  /* ── Policies (security tab, loaded on demand) ──────────────────── */
  const [policies, setPolicies]             = useState(null);
  const [policiesLoaded, setPoliciesLoaded] = useState(false);
  const [policiesError, setPoliciesError]   = useState(false);

  /* ── Domain remove confirm ─────────────────────────────────────────── */
  const [removeDomainTarget, setRemoveDomainTarget] = useState(null);

  /* ── Danger zone ──────────────────────────────────────────────────── */
  const [archiveConfirm, setArchiveConfirm] = useState(false);
  const [archiving, setArchiving]           = useState(false);
  const [deleteConfirm, setDeleteConfirm]   = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting]             = useState(false);

  /* ── System tab ──────────────────────────────────────────────────── */
  const [copied, setCopied] = useState(false);

  /* ── Regional tab controlled form ───────────────────────────────── */
  const [localeDirty, setLocaleDirty]   = useState(false);
  const [localeSaving, setLocaleSaving] = useState(false);
  const savedLocaleRef = useRef({
    language: 'English (US)', dateFormat: 'DD/MM/YYYY',
    currency: 'INR (₹)', firstDayOfWeek: 'Monday', timezone: 'IST (UTC+5:30)',
  });

  /* ── General tab controlled form ────────────────────────────────── */
  const [generalForm, setGeneralForm] = useState(() => ({
    name:        org?.name        ?? '',
    description: org?.description ?? '',
    industry:    org?.industry    ?? '',
    website:     org?.website     ?? '',
    country:     org?.country     ?? '',
    timezone:    org?.timezone    ?? '',
  }));
  const [generalDirty, setGeneralDirty]   = useState(false);
  const [generalSaving, setGeneralSaving] = useState(false);
  const generalInitRef = useRef(false);

  /* ── Data loading ─────────────────────────────────────────────────── */
  const loadSettings = useCallback(async () => {
    if (!orgId || settingsLoaded) return;
    try {
      const s = await orgApi.getSettings(orgId);
      if (!s) return;
      const localeData = {
        language:     s.language     || 'English (US)',
        dateFormat:   s.dateFormat   || 'DD/MM/YYYY',
        currency:     s.currency     || 'INR (₹)',
        firstDayOfWeek: s.firstDayOfWeek || 'Monday',
        timezone:     s.timezone     || 'IST (UTC+5:30)',
      };
      setLocaleForm(localeData);
      savedLocaleRef.current = localeData;
      const notifData = {
        notifyInvite:  s.notifyInvite  ?? true,
        notifyLogin:   s.notifyLogin   ?? true,
        notifyLicense: s.notifyLicense ?? true,
        notifyAudit:   s.notifyAudit   ?? false,
      };
      setNotifForm(notifData);
      notifInitRef.current = notifData;
      if (Array.isArray(s.allowedDomains)) setDomains(s.allowedDomains);
    } catch {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load settings' });
    } finally {
      setSettingsLoaded(true);
    }
  }, [orgId, settingsLoaded, toast]);

  const loadPolicies = useCallback(async () => {
    if (!orgId) return;
    setPoliciesLoaded(false);
    setPoliciesError(false);
    try {
      const p = await orgPoliciesApi.get(orgId);
      setPolicies(p);
    } catch { setPoliciesError(true); }
    finally { setPoliciesLoaded(true); }
  }, [orgId]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  useEffect(() => {
    if (tab === 'security') loadPolicies();
  }, [tab, loadPolicies]);

  useEffect(() => {
    if (!org || generalInitRef.current) return;
    generalInitRef.current = true;
    setGeneralForm({
      name:        org.name        ?? '',
      description: org.description ?? '',
      industry:    org.industry    ?? '',
      website:     org.website     ?? '',
      country:     org.country     ?? '',
      timezone:    org.timezone    ?? '',
    });
  }, [org]);

  /* All hooks before conditional returns */
  const emailProviderScopedApi = useMemo(() => ({
    list:       ()           => orgEmailProviderApi.list(orgId),
    create:     (body)       => orgEmailProviderApi.create(orgId, body),
    update:     (id, body)   => orgEmailProviderApi.update(orgId, id, body),
    remove:     (id)         => orgEmailProviderApi.remove(orgId, id),
    test:       (id)         => orgEmailProviderApi.test(orgId, id),
    activate:   (id)         => orgEmailProviderApi.activate(orgId, id),
    deactivate: (id)         => orgEmailProviderApi.deactivate(orgId, id),
  }), [orgId]);

  if (runtimeLoading || !org) return null;

  const canView = perm.has(ORG.SETTINGS_VIEW);
  const canEdit = perm.has(ORG.SETTINGS_EDIT);

  if (!canView) return (
    <div className="nx-cc-page">
      <DeniedState
        reason="insufficient_permission"
        message="Organization Settings require the Settings View capability. Contact your org admin."
      />
    </div>
  );

  /* ── Cross-module navigation with return breadcrumb ──────────────── */
  const withReturn = (path) => {
    const sep = path.includes('?') ? '&' : '?';
    const rt  = encodeURIComponent(`/organization/${orgId}/settings?tab=${tab}`);
    return `${path}${sep}returnTo=${rt}&returnLabel=Settings`;
  };

  /* ── Handlers ──────────────────────────────────────────────────────── */
  const setGeneralField = (field, value) => {
    setGeneralForm(f => ({ ...f, [field]: value }));
    setGeneralDirty(true);
  };

  const handleSaveGeneral = async () => {
    if (!canEdit || generalSaving) return;
    const name = generalForm.name.trim();
    if (!name) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Organization name is required' });
      return;
    }
    const website = generalForm.website.trim();
    if (website && !/^https?:\/\/.+\..+/.test(website)) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Invalid website URL', desc: 'Must start with https:// or http://' });
      return;
    }
    setGeneralSaving(true);
    try {
      await orgApi.update(orgId, {
        name,
        description: generalForm.description.trim(),
        industry:    generalForm.industry.trim(),
        website,
        country:     generalForm.country,
        timezone:    generalForm.timezone,
      });
      setGeneralDirty(false);
      refreshRuntime();
      toast({ tone: 'success', icon: 'Check', title: 'Organization profile saved' });
    } catch (err) {
      const isNameConflict = err?.status === 409 || err?.body?.code === 'ORG_NAME_CONFLICT';
      if (isNameConflict) {
        toast({ tone: 'error', icon: 'AlertCircle', title: 'Name already taken', desc: `An organization named "${name}" already exists. Choose a different name.` });
      } else {
        toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to save profile', desc: err.message });
      }
    } finally {
      setGeneralSaving(false);
    }
  };

  const handleDiscardGeneral = () => {
    setGeneralForm({
      name:        org.name        ?? '',
      description: org.description ?? '',
      industry:    org.industry    ?? '',
      website:     org.website     ?? '',
      country:     org.country     ?? '',
      timezone:    org.timezone    ?? '',
    });
    setGeneralDirty(false);
  };

  const setLocaleField = (field, value) => {
    setLocaleForm(f => ({ ...f, [field]: value }));
    setLocaleDirty(true);
  };

  const handleSaveLocale = async () => {
    if (!canEdit || localeSaving) return;
    setLocaleSaving(true);
    try {
      await orgApi.updateLocalization(orgId, localeForm);
      savedLocaleRef.current = { ...localeForm };
      setLocaleDirty(false);
      toast({ tone: 'success', icon: 'Check', title: 'Regional settings saved' });
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to save', desc: e.message });
    } finally {
      setLocaleSaving(false);
    }
  };

  const handleDiscardLocale = () => {
    setLocaleForm({ ...savedLocaleRef.current });
    setLocaleDirty(false);
  };

  const handleSaveNotif = async () => {
    if (!canEdit || !notifDirty) return;
    try {
      await orgApi.updatePreferences(orgId, notifForm);
      notifInitRef.current = { ...notifForm };
      setNotifDirty(false);
      toast({ tone: 'success', icon: 'Check', title: 'Notification preferences saved' });
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to save preferences', desc: e.message });
    }
  };

  const setNotifField = (field, value) => {
    setNotifForm(f => {
      const next = { ...f, [field]: value };
      const init = notifInitRef.current;
      setNotifDirty(
        next.notifyInvite  !== init.notifyInvite  ||
        next.notifyLogin   !== init.notifyLogin   ||
        next.notifyLicense !== init.notifyLicense ||
        next.notifyAudit   !== init.notifyAudit,
      );
      return next;
    });
  };

  const addDomain = async () => {
    const d = newDomain.trim().toLowerCase();
    if (!d || domains.includes(d)) return;
    if (!/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z]{2,})+$/.test(d)) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Invalid domain', desc: 'Enter a valid domain like company.com' });
      return;
    }
    const prev    = domains;
    const updated = [...domains, d];
    setDomains(updated);
    setNewDomain('');
    try {
      await orgApi.update(orgId, { allowedDomains: updated });
      toast({ tone: 'success', icon: 'Globe', title: 'Domain added' });
    } catch (e) {
      setDomains(prev);
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to add domain', desc: e.message });
    }
  };

  const removeDomain = async (domain) => {
    const prev    = domains;
    const updated = domains.filter(x => x !== domain);
    setDomains(updated);
    try {
      await orgApi.update(orgId, { allowedDomains: updated });
      toast({ tone: 'warning', icon: 'X', title: 'Domain removed' });
    } catch { setDomains(prev); }
  };

  const handleCopyOrgId = () => {
    navigator.clipboard.writeText(org.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleArchive = async () => {
    setArchiving(true);
    try {
      await orgApi.archive(orgId);
      toast({ tone: 'warning', icon: 'Archive', title: 'Organization archived', desc: 'All access has been suspended.' });
      navigate('/platform');
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Archive failed', desc: e.message });
    } finally { setArchiving(false); }
  };

  const handleDelete = async () => {
    if (deleteConfirm !== org.name || !canEdit) return;
    setDeleting(true);
    try {
      await orgApi.remove(orgId);
      navigate('/platform');
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Delete failed', desc: e.message });
    } finally { setDeleting(false); }
  };

  const featureOverrides = parseFeatureOverrides(org.featureOverrides);

  const dateFormatExample = (() => {
    const n = new Date();
    const d = n.getDate();
    const mo = n.getMonth();
    const y = n.getFullYear();
    const mNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const dd = String(d).padStart(2, '0');
    const mm = String(mo + 1).padStart(2, '0');
    switch (localeForm.dateFormat) {
      case 'DD/MM/YYYY': return `${dd}/${mm}/${y}`;
      case 'MM/DD/YYYY': return `${mm}/${dd}/${y}`;
      case 'YYYY-MM-DD': return `${y}-${mm}-${dd}`;
      case 'D MMM YYYY': return `${d} ${mNames[mo]} ${y}`;
      default: return '';
    }
  })();

  /* ── Render ────────────────────────────────────────────────────────── */
  return (
    <div className="nx-cc-page nx-settings-page">
      <PageHead title="Settings" sub={'Configuration for ' + org.name} />
      <TabBar tabs={TABS} active={tab} onChange={handleTabChange} />
      <div className="nx-cc-tab-body">

        {/* ── GENERAL ─────────────────────────────────────────────────── */}
        {tab === 'general' && (
          <>
            <SectionCard
              title={<>Organization Profile{generalDirty && <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--nx-warning)', marginLeft: 8 }}>· Unsaved changes</span>}</>}
              desc="Core identity details visible across workspaces, notifications, and API integrations."
            >
              {/* ── Identity ──────────────────────────────────────────── */}
              <div style={{ marginBottom: 24 }}>
                <p style={{ margin: '0 0 12px', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--nx-text-muted)' }}>Identity</p>
                <div className="nx-form--2col" style={{ gap: '16px 24px' }}>
                  <div className="nx-form-row">
                    <label htmlFor="s-name">Organization Name<span className="nx-form-req" aria-hidden="true"> *</span></label>
                    <input id="s-name" className="nx-input" value={generalForm.name}
                      disabled={!canEdit} aria-required="true"
                      placeholder="Your organization's official name"
                      onChange={e => setGeneralField('name', e.target.value)} />
                  </div>
                  <div className="nx-form-row">
                    <label htmlFor="s-slug">Identifier</label>
                    <input id="s-slug" className="nx-input" value={org.slug || org.id} readOnly
                      style={{ background: 'var(--nx-surface-alt)', cursor: 'default' }} />
                    <p className="nx-muted nx-mini" style={{ marginTop: 4 }}>Auto-generated. Used in platform URLs and API references.</p>
                  </div>
                </div>
              </div>

              {/* ── Details ───────────────────────────────────────────── */}
              <div style={{ marginBottom: 24, paddingTop: 20, borderTop: '1px solid var(--nx-divider)' }}>
                <p style={{ margin: '0 0 12px', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--nx-text-muted)' }}>Details</p>
                <div className="nx-form--2col" style={{ gap: '16px 24px' }}>
                  <div className="nx-form-row nx-form-row--full">
                    <label htmlFor="s-desc">Description</label>
                    <textarea id="s-desc" className="nx-input" value={generalForm.description}
                      rows={4} disabled={!canEdit}
                      style={{ height: 92, minHeight: 92, padding: '10px 12px', resize: 'vertical', lineHeight: 1.5 }}
                      placeholder="A brief description of your organization's purpose and scope."
                      onChange={e => setGeneralField('description', e.target.value)} />
                  </div>
                  <div className="nx-form-row">
                    <label htmlFor="s-industry">Industry</label>
                    <input id="s-industry" className="nx-input" value={generalForm.industry}
                      disabled={!canEdit}
                      placeholder="e.g. Technology, Healthcare, Finance"
                      onChange={e => setGeneralField('industry', e.target.value)} />
                  </div>
                  <div className="nx-form-row">
                    <label htmlFor="s-website">Website</label>
                    <input id="s-website" className="nx-input" type="url" value={generalForm.website}
                      disabled={!canEdit} placeholder="https://yourcompany.com"
                      onChange={e => setGeneralField('website', e.target.value)} />
                  </div>
                </div>
              </div>

              {/* ── Localization ──────────────────────────────────────── */}
              <div style={{ paddingTop: 20, borderTop: '1px solid var(--nx-divider)' }}>
                <p style={{ margin: '0 0 12px', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--nx-text-muted)' }}>Localization</p>
                <div className="nx-form--2col" style={{ gap: '16px 24px' }}>
                  <div className="nx-form-row">
                    <label htmlFor="s-country">Country</label>
                    <select id="s-country" className="nx-input" value={generalForm.country}
                      disabled={!canEdit}
                      onChange={e => setGeneralField('country', e.target.value)}>
                      {['India','UAE','Singapore','USA','UK','Australia','Germany','Canada'].map(c => (
                        <option key={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div className="nx-form-row">
                    <label htmlFor="s-tz">Organization Timezone</label>
                    <select id="s-tz" className="nx-input" value={generalForm.timezone}
                      disabled={!canEdit}
                      onChange={e => setGeneralField('timezone', e.target.value)}>
                      {['IST (UTC+5:30)','GST (UTC+4)','SGT (UTC+8)','EST (UTC-5)','PST (UTC-8)','GMT (UTC+0)','CET (UTC+1)'].map(tz => (
                        <option key={tz}>{tz}</option>
                      ))}
                    </select>
                    <p className="nx-muted nx-mini" style={{ marginTop: 4 }}>Used for scheduling and timestamps. Member locale settings are in the Regional tab.</p>
                  </div>
                </div>
              </div>

              {/* ── Actions ───────────────────────────────────────────── */}
              {canEdit && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--nx-divider)' }}>
                  {generalDirty && (
                    <Btn variant="ghost" onClick={handleDiscardGeneral} disabled={generalSaving}>Discard</Btn>
                  )}
                  <Btn
                    variant="primary"
                    icon={generalSaving ? 'Loader' : 'Save'}
                    onClick={handleSaveGeneral}
                    disabled={!generalDirty || generalSaving}
                  >
                    {generalSaving ? 'Saving…' : 'Save Changes'}
                  </Btn>
                </div>
              )}
            </SectionCard>
            <RefCard
              icon="Palette"
              title="Branding"
              desc="Logo, colors, and white-label settings for this organization."
              linkLabel="Go to Branding"
              onClick={() => navigate(withReturn(`/organization/${orgId}/branding`))}
            />
          </>
        )}

        {/* ── REGIONAL ──────────────────────────────────────────────────── */}
        {tab === 'regional' && (
          <SectionCard
            title={<>Regional & Localization{localeDirty && <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--nx-warning)', marginLeft: 8 }}>· Unsaved changes</span>}</>}
            desc="Default locale and format settings applied to all workspace members."
          >
            {/* ── Locale ──────────────────────────────────────────────── */}
            <div style={{ marginBottom: 24 }}>
              <p style={{ margin: '0 0 12px', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--nx-text-muted)' }}>Language & Timezone</p>
              <div className="nx-form--2col" style={{ gap: '16px 24px' }}>
                <div className="nx-form-row">
                  <label htmlFor="r-language">Language</label>
                  <select id="r-language" className="nx-input" value={localeForm.language} disabled={!canEdit}
                    onChange={e => setLocaleField('language', e.target.value)}>
                    {['English (US)','English (UK)','Hindi','Arabic','German','French','Spanish','Portuguese'].map(l => (
                      <option key={l}>{l}</option>
                    ))}
                  </select>
                  <p className="nx-muted nx-mini" style={{ marginTop: 4 }}>Interface language for all workspace members.</p>
                </div>
                <div className="nx-form-row">
                  <label htmlFor="r-tz">Default Member Timezone</label>
                  <select id="r-tz" className="nx-input" value={localeForm.timezone} disabled={!canEdit}
                    onChange={e => setLocaleField('timezone', e.target.value)}>
                    {['IST (UTC+5:30)','GST (UTC+4)','SGT (UTC+8)','EST (UTC-5)','PST (UTC-8)','GMT (UTC+0)','CET (UTC+1)'].map(tz => (
                      <option key={tz}>{tz}</option>
                    ))}
                  </select>
                  <p className="nx-muted nx-mini" style={{ marginTop: 4 }}>The default timezone for displaying dates and times to workspace members. Members who have set a personal timezone will see their own preference; others use this value. Platform scheduling, automations, and API event timestamps are governed by Organization Timezone in General settings.</p>
                </div>
              </div>
            </div>

            {/* ── Date & Time ─────────────────────────────────────────── */}
            <div style={{ marginBottom: 24, paddingTop: 20, borderTop: '1px solid var(--nx-divider)' }}>
              <p style={{ margin: '0 0 12px', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--nx-text-muted)' }}>Date & Time</p>
              <div className="nx-form--2col" style={{ gap: '16px 24px' }}>
                <div className="nx-form-row">
                  <label htmlFor="r-datefmt">Date Format</label>
                  <select id="r-datefmt" className="nx-input" value={localeForm.dateFormat} disabled={!canEdit}
                    onChange={e => setLocaleField('dateFormat', e.target.value)}>
                    {['DD/MM/YYYY','MM/DD/YYYY','YYYY-MM-DD','D MMM YYYY'].map(d => (
                      <option key={d}>{d}</option>
                    ))}
                  </select>
                  {dateFormatExample && <p className="nx-muted nx-mini" style={{ marginTop: 4 }}>Example: {dateFormatExample}</p>}
                </div>
                <div className="nx-form-row">
                  <label htmlFor="r-weekstart">First Day of Week</label>
                  <select id="r-weekstart" className="nx-input" value={localeForm.firstDayOfWeek} disabled={!canEdit}
                    onChange={e => setLocaleField('firstDayOfWeek', e.target.value)}>
                    {['Monday','Sunday','Saturday'].map(d => <option key={d}>{d}</option>)}
                  </select>
                  <p className="nx-muted nx-mini" style={{ marginTop: 4 }}>Starting column in calendar views and date pickers.</p>
                </div>
              </div>
            </div>

            {/* ── Financial ───────────────────────────────────────────── */}
            <div style={{ paddingTop: 20, borderTop: '1px solid var(--nx-divider)' }}>
              <p style={{ margin: '0 0 12px', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--nx-text-muted)' }}>Financial</p>
              <div className="nx-form--2col" style={{ gap: '16px 24px' }}>
                <div className="nx-form-row">
                  <label htmlFor="r-currency">Currency</label>
                  <select id="r-currency" className="nx-input" value={localeForm.currency} disabled={!canEdit}
                    onChange={e => setLocaleField('currency', e.target.value)}>
                    {['INR (₹)','USD ($)','EUR (€)','GBP (£)','AED (د.إ)','SGD (S$)'].map(c => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                  <p className="nx-muted nx-mini" style={{ marginTop: 4 }}>Symbol used when displaying monetary values in CRM records and reports.</p>
                </div>
              </div>
            </div>

            {/* ── Actions ─────────────────────────────────────────────── */}
            {canEdit && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--nx-divider)' }}>
                {localeDirty && (
                  <Btn variant="ghost" onClick={handleDiscardLocale} disabled={localeSaving}>Discard</Btn>
                )}
                <Btn
                  variant="primary"
                  icon={localeSaving ? 'Loader' : 'Save'}
                  onClick={handleSaveLocale}
                  disabled={!localeDirty || localeSaving}
                >
                  {localeSaving ? 'Saving…' : 'Save Changes'}
                </Btn>
              </div>
            )}
          </SectionCard>
        )}

        {/* ── SECURITY ──────────────────────────────────────────────────── */}
        {tab === 'security' && (
          <>
            <SectionCard
              title="Security Posture"
              desc="Authentication and access policies active for this organization. To configure these policies, open Identity & Access."
            >
              <SecurityPoliciesContent
                policiesLoaded={policiesLoaded}
                policiesError={policiesError}
                policies={policies}
              />
            </SectionCard>

            <RefCard
              icon="Lock"
              title="Identity & Access"
              desc="Configure MFA enforcement, SSO providers, IP allowlists, SCIM provisioning, and session timeout policies."
              linkLabel="Open IAM"
              onClick={() => navigate(withReturn(`/organization/${orgId}/iam?tab=authentication`))}
            />
            <RefCard
              icon="ClipboardList"
              title="Audit Logs"
              desc="Review authentication events, policy changes, member activity, and security incidents."
              linkLabel="View Logs"
              onClick={() => navigate(withReturn(`/organization/${orgId}/audit`))}
            />
          </>
        )}

        {/* ── COMMUNICATION ─────────────────────────────────────────────── */}
        {tab === 'communication' && (
          <>
            <SectionCard title="Notification Preferences" desc="Email notifications sent to organization admins.">
              <div>
                <ToggleRow
                  label="Member invitation notifications"
                  desc="Notify when a new member is invited to this organization."
                  on={notifForm.notifyInvite}
                  onChange={v => setNotifField('notifyInvite', v)}
                  disabled={!canEdit}
                />
                <ToggleRow
                  label="Login alerts"
                  desc="Notify on suspicious or failed sign-in attempts."
                  on={notifForm.notifyLogin}
                  onChange={v => setNotifField('notifyLogin', v)}
                  disabled={!canEdit}
                />
                <ToggleRow
                  label="License expiry warnings"
                  desc="Notify 30 days before any license expires."
                  on={notifForm.notifyLicense}
                  onChange={v => setNotifField('notifyLicense', v)}
                  disabled={!canEdit}
                />
                <ToggleRow
                  label="Audit event alerts"
                  desc="Notify on high-severity audit events."
                  on={notifForm.notifyAudit}
                  onChange={v => setNotifField('notifyAudit', v)}
                  disabled={!canEdit}
                />
              </div>
              {canEdit && (
                <div style={{ marginTop: 12 }}>
                  <Btn variant="primary" icon="Check" disabled={!notifDirty} onClick={handleSaveNotif}>Save Preferences</Btn>
                </div>
              )}
            </SectionCard>

            <SectionCard title="System Email" desc="Transactional email providers for member invitations, password resets, MFA codes, and login alerts.">
              <EmailProviderManager api={emailProviderScopedApi} readOnly={!canEdit} />
            </SectionCard>

          </>
        )}

        {/* ── DATA & PRIVACY ─────────────────────────────────────────────── */}
        {tab === 'data' && (
          <>
            <RefCard
              icon="Archive"
              title="Backup &amp; Restore"
              desc="View backup schedule, history, encryption settings, and submit restore requests."
              linkLabel="Open"
              onClick={() => navigate(withReturn(`/organization/${orgId}/backup`))}
            />

            <SectionCard title="Allowed Email Domains" desc="Restrict member invitations to specific email domains. Leave empty to allow any domain.">
              {canEdit && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <input className="nx-input" style={{ flex: 1 }} value={newDomain}
                    onChange={e => setNewDomain(e.target.value)}
                    placeholder="e.g. company.com"
                    onKeyDown={e => { if (e.key === 'Enter') addDomain(); }} />
                  <Btn variant="secondary" icon="Plus" onClick={addDomain}>Add Domain</Btn>
                </div>
              )}
              {domains.length === 0 ? (
                <div className="nx-empty" style={{ padding: '10px 0' }}>
                  <NexusIcon name="Globe" size={15} />
                  <span>No restrictions — any email domain can be invited.</span>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {domains.map(d => (
                    <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 6, background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)' }}>
                      <NexusIcon name="Globe" size={13} style={{ color: 'var(--nx-success)' }} />
                      <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{d}</span>
                      <Pill tone="green">Allowed</Pill>
                      {canEdit && <Btn variant="ghost" size="sm" icon="X" onClick={() => setRemoveDomainTarget(d)} />}
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>

            <SectionCard title="Data Export" desc="Audit logs and member data exports.">
              <p style={{ fontSize: 13, color: 'var(--nx-text-soft)', lineHeight: 1.6, marginBottom: 12 }}>
                Exports are available to users with the <strong>Audit Export</strong> capability and include audit logs
                and member data in CSV format. Full data archives are managed via the Backup module.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn variant="ghost" icon="ClipboardList"
                  onClick={() => navigate(`/organization/${orgId}/audit`)}>
                  Audit Logs
                </Btn>
                <Btn variant="ghost" icon="HardDrive"
                  onClick={() => navigate(`/organization/${orgId}/backup`)}>
                  Backup Archives
                </Btn>
              </div>
            </SectionCard>
          </>
        )}

        {/* ── SYSTEM ─────────────────────────────────────────────────────── */}
        {tab === 'system' && (
          <>
            <SectionCard title="Organization Identity" desc="Read-only system identifiers and metadata.">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', fontSize: 13 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <div className="nx-muted nx-mini" style={{ marginBottom: 4 }}>Organization ID</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <code style={{ fontSize: 11, background: 'var(--nx-surface-alt)', padding: '4px 8px', borderRadius: 4, border: '1px solid var(--nx-border)', userSelect: 'all', fontFamily: 'monospace' }}>
                      {org.id}
                    </code>
                    <Btn variant="ghost" size="sm" icon={copied ? 'Check' : 'Copy'} onClick={handleCopyOrgId}>
                      {copied ? 'Copied' : 'Copy'}
                    </Btn>
                  </div>
                </div>
                <div>
                  <div className="nx-muted nx-mini" style={{ marginBottom: 2 }}>Slug</div>
                  <code style={{ fontSize: 12 }}>{org.slug || '—'}</code>
                </div>
                <div>
                  <div className="nx-muted nx-mini" style={{ marginBottom: 2 }}>Status</div>
                  <Pill tone={org.status === 'ACTIVE' ? 'green' : 'amber'}>{org.status || '—'}</Pill>
                </div>
                <div>
                  <div className="nx-muted nx-mini" style={{ marginBottom: 2 }}>Created</div>
                  <span>{fmtDate(org.createdAt)}</span>
                </div>
                <div>
                  <div className="nx-muted nx-mini" style={{ marginBottom: 2 }}>Region</div>
                  <span>{org.region || '—'}</span>
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Feature Flags" desc="Feature overrides applied to this organization beyond plan defaults.">
              {featureOverrides.add?.length === 0 && featureOverrides.remove?.length === 0 ? (
                <div className="nx-empty" style={{ padding: '10px 0' }}>
                  <NexusIcon name="ToggleLeft" size={16} />
                  <span>No feature overrides — using plan defaults.</span>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {(featureOverrides.add || []).map(f => (
                    <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                      <NexusIcon name="Plus" size={12} style={{ color: 'var(--nx-success)' }} />
                      <code style={{ flex: 1 }}>{f}</code>
                      <Pill tone="green">Enabled</Pill>
                    </div>
                  ))}
                  {(featureOverrides.remove || []).map(f => (
                    <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                      <NexusIcon name="Minus" size={12} style={{ color: 'var(--nx-danger)' }} />
                      <code style={{ flex: 1 }}>{f}</code>
                      <Pill tone="red">Disabled</Pill>
                    </div>
                  ))}
                </div>
              )}
              {features.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                    Resolved Features ({features.length})
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {features.map(f => <Pill key={f} tone="blue">{f}</Pill>)}
                  </div>
                </div>
              )}
            </SectionCard>

            <RefCard
              icon="ClipboardList"
              title="Audit Logs"
              desc="Full audit trail for all organization activities."
              linkLabel="View Logs"
              onClick={() => navigate(withReturn(`/organization/${orgId}/audit`))}
            />
          </>
        )}

        {/* ── DANGER ZONE ────────────────────────────────────────────────── */}
        {tab === 'danger' && (
          <div style={{ maxWidth: 720 }}>
            <div style={{ padding: 20, marginBottom: 16, border: '1px solid var(--nx-danger)', borderRadius: 8, background: 'var(--nx-danger-tint)' }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-danger)', marginBottom: 4 }}>
                Archive Organization
              </h3>
              <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', marginBottom: 14 }}>
                Suspends all access to this organization and its workspaces. Members cannot sign in.
                A Super Admin can reverse this action.
              </p>
              <Btn variant="ghost" icon="Archive" disabled={archiving || !canEdit}
                onClick={() => setArchiveConfirm(true)}>
                {archiving ? 'Archiving…' : 'Archive Organization'}
              </Btn>
            </div>

            <div style={{ padding: 20, border: '1px solid var(--nx-danger)', borderRadius: 8, background: 'var(--nx-danger-tint)' }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-danger)', marginBottom: 4 }}>
                Delete Organization
              </h3>
              <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', marginBottom: 14 }}>
                Permanently deletes this organization, all workspaces, members, data, and files.
                This action is irreversible.
              </p>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
                <input className="nx-input" style={{ flex: 1 }}
                  value={deleteConfirm}
                  onChange={e => setDeleteConfirm(e.target.value)}
                  placeholder={`Type "${org.name}" to confirm`}
                  disabled={!canEdit} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn variant="danger" icon="Trash"
                  disabled={deleteConfirm !== org.name || deleting || !canEdit}
                  onClick={() => setDeleteDialogOpen(true)}>
                  {deleting ? 'Deleting…' : 'Delete Organization'}
                </Btn>
                {deleteConfirm && (
                  <Btn variant="ghost" icon="X" onClick={() => setDeleteConfirm('')}>Cancel</Btn>
                )}
              </div>
            </div>
          </div>
        )}

      </div>

      {removeDomainTarget && (
        <ConfirmDialog
          title="Remove domain?"
          desc={`Remove "${removeDomainTarget}" from the allowed domains list? Members from this domain may no longer be invited.`}
          danger
          confirmLabel="Remove"
          onConfirm={() => { removeDomain(removeDomainTarget); setRemoveDomainTarget(null); }}
          onCancel={() => setRemoveDomainTarget(null)}
        />
      )}

      {archiveConfirm && (
        <ConfirmDialog
          title="Archive Organization?"
          desc="All members will lose access immediately. Workspaces will be suspended. A Super Admin can reverse this action."
          danger
          busy={archiving}
          confirmLabel="Archive Organization"
          onConfirm={handleArchive}
          onCancel={() => setArchiveConfirm(false)}
        />
      )}

      {deleteDialogOpen && (
        <ConfirmDialog
          title="Permanently Delete Organization?"
          desc={`All workspaces, members, data, and files for "${org.name}" will be immediately and irreversibly deleted. This cannot be undone.`}
          danger
          busy={deleting}
          confirmLabel="Delete Organization"
          onConfirm={handleDelete}
          onCancel={() => setDeleteDialogOpen(false)}
        />
      )}
    </div>
  );
}
